import _ from "lodash";
import React from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classes from "./StudentList.scss";
import I18nHOC from "UIComponents/I18nHOC";
import { Avatar, Checkbox, EmptyState, RadioButton } from "@toddle-edu/ds-web";
import { AVATAR_COLORS } from "Constants/colorConstants";
import {
  AddStudentIllustration,
  NoSearchInFilterIllustration,
} from "@toddle-edu/ds-theme";
import { getFormattedStudentName } from "Utils/GetFormattedStudentName";
import { getDefaultStudentNameOrderPreference } from "modules/CommonSelectors";
import { hashString } from "Utils";
import classNames from "classnames";
import { StudentAlertsWrapper } from "AppComponents/StudentAlerts";

const emptyStateSubtitleStyle = {
  lineHeight: "24px",
};
const BLANK_OBJECT = {};
const StudentItem = React.memo(props => {
  const {
    item,
    isChecked,
    isDisabled,
    onItemCheckBoxClick,
    isIndeterminate = false,
    studentSelectionMode,
    t,
    defaultStudentNameOrderPreference,
    showNewDesign,
    showNewDesignV2,
  } = props;

  const { id, profileImage, isArchived } = item || {};

  const studentName = getFormattedStudentName({
    student: item,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
    showStudentUID: true,
  });

  const studentId = _.get(item, "id", null);
  const alertTypes = _.get(item, "alertTypes", []);

  const studentItemClass = classNames({
    [classes.studentItem]: !showNewDesign && !showNewDesignV2,
    [classes.studentItemV2]: showNewDesign && !showNewDesignV2,
    [classes.studentItemV3]: showNewDesignV2,
  });

  return (
    <div
      className={studentItemClass}
      onClick={() => onItemCheckBoxClick(props.item)}
      data-test-id={"journal-postcreation-tagstudentitem-" + id}
      data-is-selected={isChecked}
    >
      <div className={classes.itemLeftContainer}>
        <div className={classes.avatarContainer}>
          <Avatar
            name={studentName}
            shade={"light"}
            src={profileImage}
            iconType={id === "ALL" ? "group" : ""}
            color={
              isArchived || id === "ALL"
                ? "neutral"
                : AVATAR_COLORS[hashString(id) % AVATAR_COLORS.length]
            }
          />
        </div>
        <div className={classes.statusWrapper}>
          <div className={classes.nameText}>
            <StudentAlertsWrapper studentId={studentId} alertTypes={alertTypes}>
              {studentName}
            </StudentAlertsWrapper>
          </div>
          {isArchived && (
            <div className={"archivedLabel"}>{t("common:archived")}</div>
          )}
        </div>
      </div>
      <div className={classes.itemRightContainer}>
        {_.isEqual(studentSelectionMode, "SINGLE") ? (
          <RadioButton
            size={"xx-small"}
            checked={isChecked}
            disabled={isDisabled}
            onClick={() => onItemCheckBoxClick(props.item)}
            shouldStopPropagation={true}
          />
        ) : (
          <Checkbox
            dsVersion="2.0"
            size="medium"
            isChecked={isChecked}
            disabled={isDisabled}
            isIndeterminate={isIndeterminate}
          />
        )}
      </div>
    </div>
  );
});

StudentItem.displayName = "StudentItem";

class StudentList extends React.PureComponent {
  render() {
    const {
      studentListRef,
      students,
      selectedStudentIds,
      onItemCheckBoxClick,
      searchText,
      isAllSelected,
      userInfo: { id: userId, user_type: userType },
      studentSelectionMode = "MULTIPLE",
      t,
      defaultStudentNameOrderPreference,
      showNewDesign = false,
      showNewDesignV2 = false,
      createdBy,
    } = this.props;
    const studentLength = _.get(students, "length", 0);
    return (
      <div className={classes.container}>
        {studentLength > 0 ? (
          <div className={classes.listContainer} ref={studentListRef}>
            {!searchText && _.isEqual(studentSelectionMode, "MULTIPLE") && (
              <StudentItem
                t={t}
                key={"ALL"}
                item={{
                  id: "ALL",
                  firstName: showNewDesignV2 ? t("allStudents") : t("all"),
                  lastName: "",
                  middleName: "",
                }}
                isChecked={isAllSelected}
                onItemCheckBoxClick={onItemCheckBoxClick}
                isIndeterminate={
                  !isAllSelected && _.size(_.compact(selectedStudentIds), 0) > 0
                }
                showNewDesign={showNewDesign}
                showNewDesignV2={showNewDesignV2}
              />
            )}
            {/* Make sure student cant uncheck itself by disabling the checkbox*/}
            {_.map(students, item => {
              const isChecked = _.includes(selectedStudentIds, item.node.id);
              return (
                <StudentItem
                  key={item.node.id}
                  item={item.node}
                  isChecked={isChecked}
                  isDisabled={
                    (userType == "student" && userId == item.node.id) ||
                    createdBy?.id == item.node.id
                  }
                  onItemCheckBoxClick={onItemCheckBoxClick}
                  t={t}
                  studentSelectionMode={studentSelectionMode}
                  defaultStudentNameOrderPreference={
                    defaultStudentNameOrderPreference
                  }
                  showNewDesign={showNewDesign}
                  showNewDesignV2={showNewDesignV2}
                />
              );
            })}
          </div>
        ) : searchText ? (
          <div className={classes.emptyStateContainer}>
            <EmptyState
              illustration={NoSearchInFilterIllustration}
              title={t("common:noResultHeading")}
              subtitle={t("common:student_search_empty_state_description")}
              subtitleStyle={emptyStateSubtitleStyle}
              size="small"
            />
          </div>
        ) : (
          <div className={classes.emptyStateContainer}>
            <EmptyState
              illustration={AddStudentIllustration}
              title={t("common:no_students_to_choose")}
              subtitle={t("common:request_admin_to_add_students")}
              subtitleStyle={emptyStateSubtitleStyle}
            />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const createdBy = _.get(state, "post.postDetails.createdBy", BLANK_OBJECT);
  return {
    defaultStudentNameOrderPreference,
    createdBy,
  };
};

export default compose(
  connect(mapStateToProps, null),
  I18nHOC({ resource: "common" })
)(StudentList);
